import React, { KeyboardEvent, MouseEvent, ReactNode, useEffect, useMemo, useRef } from 'react';
import { Box, ClickAwayListener, Popper } from '@mui/material';
import './chatContextMenu.scss';

export interface iChatMenuItem {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

export type ChatMenuAnchor = { element: HTMLElement } | { x: number; y: number };

interface ChatContextMenuProps {
  anchor: ChatMenuAnchor | null;
  items: iChatMenuItem[];
  onClose: () => void;
}

const toVirtualElement = (anchor: ChatMenuAnchor) => {
  if ('element' in anchor) return anchor.element;
  const { x, y } = anchor;
  return { getBoundingClientRect: () => new DOMRect(x, y, 0, 0) };
};

const ChatContextMenu: React.FC<ChatContextMenuProps> = ({ anchor, items, onClose }) => {
  const anchorEl = useMemo(() => (anchor ? toVirtualElement(anchor) : null), [anchor]);
  const open = Boolean(anchorEl) && items.length > 0;
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open, onClose]);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={anchor && 'element' in anchor ? 'bottom-end' : 'bottom-start'}
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      className="chatContextMenuPopper"
    >
      <ClickAwayListener onClickAway={onClose} mouseEvent="onMouseDown">
        <Box
          ref={menuRef}
          role="menu"
          className="chatContextMenu"
          onClick={(event: MouseEvent) => event.stopPropagation()}
          onContextMenu={(event: MouseEvent) => event.preventDefault()}
          onKeyDown={(event: KeyboardEvent) => event.key === 'Escape' && onClose()}
        >
          {items.map((item) => (
            <Box
              key={item.key}
              component="button"
              type="button"
              role="menuitem"
              className="chatContextMenuItem"
              onClick={() => {
                onClose();
                item.onClick();
              }}
            >
              <span className="chatContextMenuLabel">{item.label}</span>
              <span className="chatContextMenuIcon">{item.icon}</span>
            </Box>
          ))}
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default ChatContextMenu;
